import { useEffect, useMemo, ReactElement, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';

import CheckoutHeader from './CheckoutHeader';
import SpaceStickyFooter from '../../components/SpaceStickyFooter';
import BookingPending from '../../components/ProcessPending/index';

import ReservationCard from '../../components/ReservationCard';
import { TrackingEventEnum } from '../../utils/analytics';
import useAnalytics from '../../hooks/useAnalytics';
import useReduxKey from '../../hooks/useReduxKey';

import './Overview.scss';
import { FilterState } from '../../reducers/filter';
import { Pricing } from '../../reducers/pricing';
import { PriceContext } from '../../contexts/PriceProvider';

export default function OverviewPage(): ReactElement {
  const navigate = useNavigate();
  const {
    reservationType, selectedTimeSlot,
    groupSize, selectedSpace,
  } = useReduxKey<FilterState>('filterState');
  const { t } = useTranslation();

  const { userReservationTypes, paymentsEnabled, showPayments } = useReduxKey('configuration');
  const { priceCalculationProcessed } = useReduxKey<Pricing>('pricing');
  const { trackEvent } = useAnalytics();
  const { state } = useLocation();
  const { total } = useContext(PriceContext);

  const shouldSkipPaymentStep = (paymentsEnabled && total === 0) || !paymentsEnabled;
  const footerButtenLabel = !shouldSkipPaymentStep ? 'pay' : 'submit';

  const paymentWindow = useRef<Window | null>(null);

  const openPaymentWindow = () => {
    if (paymentWindow.current) {
      paymentWindow.current.close();
    }

    const paymentRedirectTranslations = {
      translations: {
        payment_redirect_message: t('payment_redirect_message', 'You will be redirected to the payment platform shortly...'),
        payment_do_not_close_page: t('payment_do_not_close_page', 'Please do not close, refresh or navigate away from this page.'),
      },
    };

    // We open the payment window in a new tab at the user's click to avoid popup blockers, we will set the URL later
    const paymentWindowURL = `${window.location.origin}/payment-redirect.html`;
    paymentWindow.current = window.open(paymentWindowURL, 'paymentWindow');

    if (paymentWindow.current) {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data?.received) {
          clearInterval(sendTranslationsInterval);
          window.removeEventListener('message', handleMessage);
        }
      };

      const sendTranslationsInterval = setInterval(() => {
        if (paymentWindow.current && !paymentWindow.current.closed) {
          paymentWindow.current.postMessage(paymentRedirectTranslations, window.location.origin);
        }
      }, 500);

      window.addEventListener('message', handleMessage);
    }
  }

  useEffect(() => {
    trackEvent(TrackingEventEnum.SCHEDULE_OVERVIEW);
  }, [trackEvent]);

  const selectedReservationTypeName = useMemo(() => {
    const userType = userReservationTypes.find((type) =>
      type.reservation_type === reservationType);
    return userType?.name;
  }, [userReservationTypes, reservationType]);

  if (selectedSpace === null) {
    return <span>{t('coming_up')}</span>;
  }

  const shouldDisplayPage = !showPayments || (showPayments && priceCalculationProcessed);

  return shouldDisplayPage ? (
    <>
      <CheckoutHeader step={state.step} />

      <div className="container-checkout-overview">
        <ReservationCard
          space={selectedSpace}
          reservation={{ quantity: groupSize }}
          selectedTimeSlot={selectedTimeSlot}
          reservationType={selectedReservationTypeName}
        />
      </div>
      {!shouldSkipPaymentStep ? (
        <SpaceStickyFooter label={t(footerButtenLabel)} navigate={navigate} actionCallback={openPaymentWindow} paymentWindowRef={paymentWindow} next="/bookingProgress" nextStep={state.step + 1}/>
      ) : (
        <SpaceStickyFooter label={t(footerButtenLabel)} navigate={navigate} next="/bookingProgress" nextStep={state.step + 1}/>
      )}
    </>
  ) : (
    <BookingPending processName="booking" />
  );
}